import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Modal, Spin, message } from '@surecompanies/core_components'
import {
  CloudUploadOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileOutlined,
  FilePdfOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useEditDocumentStaging } from '../../PlanOverrides/hooks/useEditDocumentStaging'
import { useOverrideDocuments } from '../../PlanOverrides/hooks/useOverrideDocuments'
import { getDocumentDownloadUrl } from '../../PlanOverrides/api/plan-override-documents-api'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Staging = ReturnType<typeof useEditDocumentStaging>

type PreviewState = {
  url: string
  name: string
  contentType: string
  revoke?: boolean
}

// ---------------------------------------------------------------------------
// Helpers & constants
// ---------------------------------------------------------------------------

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp'])

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function canPreviewType(contentType: string | undefined): boolean {
  if (!contentType) return false
  return contentType.startsWith('image/') || contentType === 'application/pdf'
}

const FILE_ROW_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 10px',
  background: '#fff',
  borderBottom: '1px solid #f1f5f9',
}

const FILE_NAME_STYLE: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'rgba(0,0,0,0.85)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const FILE_META_STYLE: React.CSSProperties = {
  fontSize: 11,
  color: 'rgba(0,0,0,0.35)',
}

const ACTION_BTN_STYLE: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'rgba(0,0,0,0.3)',
  fontSize: 16,
  padding: '2px 6px',
}

export const ICON_BOX_STYLE: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 5,
  background: '#f1f5f9',
  border: '1px solid #e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  fontSize: 14,
  color: '#64748b',
}

const EVIDENCE_LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

export function FilePreview({ file }: { file: File }) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!IMAGE_TYPES.has(file.type)) return
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file])

  if (url) {
    return (
      <img
        src={url}
        alt={file.name}
        style={{
          width: 40,
          height: 40,
          objectFit: 'cover',
          borderRadius: 6,
          border: '1px solid #e5e7eb',
          flexShrink: 0,
        }}
      />
    )
  }

  const isPdf = file.type === 'application/pdf'
  return (
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 6,
        background: isPdf ? '#fef2f2' : '#f1f5f9',
        border: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: 18,
        color: isPdf ? '#dc2626' : '#64748b',
      }}
    >
      {isPdf ? <FilePdfOutlined /> : <FileOutlined />}
    </div>
  )
}

function EvidenceFileRow({
  thumbnail,
  name,
  meta,
  canPreview,
  onPreview,
  onDownload,
  onRemove,
}: {
  thumbnail: React.ReactNode
  name: string
  meta?: React.ReactNode
  canPreview: boolean
  onPreview: () => void
  onDownload: () => void
  onRemove?: () => void
}) {
  return (
    <div style={FILE_ROW_STYLE}>
      <div style={{ flexShrink: 0 }}>{thumbnail}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={FILE_NAME_STYLE}>{name}</div>
        {meta != null && <div style={FILE_META_STYLE}>{meta}</div>}
      </div>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}
      >
        {canPreview && (
          <button
            type="button"
            onClick={onPreview}
            aria-label={`Preview ${name}`}
            title="Preview"
            style={ACTION_BTN_STYLE}
          >
            <EyeOutlined style={{ fontSize: 13 }} />
          </button>
        )}
        <button
          type="button"
          onClick={onDownload}
          aria-label={`Download ${name}`}
          title="Download"
          style={ACTION_BTN_STYLE}
        >
          <DownloadOutlined style={{ fontSize: 13 }} />
        </button>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${name}`}
            title="Remove"
            style={ACTION_BTN_STYLE}
          >
            ×
          </button>
        )}
      </div>
    </div>
  )
}

function EvidencePreviewModal({
  preview,
  loading,
  onClose,
}: {
  preview: PreviewState | null
  loading: boolean
  onClose: () => void
}) {
  return (
    <Modal
      open={!!preview || loading}
      onCancel={onClose}
      footer={null}
      title={preview?.name ?? 'Preview'}
      width={820}
      centered
      destroyOnClose
    >
      {loading || !preview ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin />
        </div>
      ) : preview.contentType.startsWith('image/') ? (
        <img
          src={preview.url}
          alt={preview.name}
          style={{
            maxWidth: '100%',
            maxHeight: '70vh',
            display: 'block',
            margin: '0 auto',
            borderRadius: 8,
          }}
        />
      ) : preview.contentType === 'application/pdf' ? (
        <iframe
          src={preview.url}
          title={preview.name}
          style={{ width: '100%', height: '70vh', border: 'none' }}
        />
      ) : (
        <a href={preview.url} target="_blank" rel="noreferrer">
          Open {preview.name}
        </a>
      )}
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

export function InlineEvidenceSection({
  ruleId,
  staging,
  localFiles,
  isReadOnly,
  ruleBase,
  onAddLocal,
  onRemoveLocal,
}: {
  ruleId: string | null
  staging: Staging
  localFiles: File[]
  isReadOnly: boolean
  ruleBase?: string
  onAddLocal: (files: File[]) => void
  onRemoveLocal: (idx: number) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const { documents, isLoading } = useOverrideDocuments(
    ruleId ?? undefined,
    ruleBase
  )

  const [preview, setPreview] = useState<PreviewState | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const closePreview = useCallback(() => {
    setPreview((prev) => {
      if (prev?.revoke) URL.revokeObjectURL(prev.url)
      return null
    })
    setPreviewLoading(false)
  }, [])

  const openLocalPreview = useCallback((file: File) => {
    const url = URL.createObjectURL(file)
    setPreview({ url, name: file.name, contentType: file.type, revoke: true })
  }, [])

  const downloadLocal = useCallback((file: File) => {
    const url = URL.createObjectURL(file)
    const a = document.createElement('a')
    a.href = url
    a.download = file.name
    a.click()
    URL.revokeObjectURL(url)
  }, [])

  const openDocPreview = useCallback(
    async (docId: string, fileName: string, contentType: string) => {
      if (!ruleId) return
      setPreviewLoading(true)
      try {
        const url = await getDocumentDownloadUrl(ruleId, docId, ruleBase)
        setPreview({ url, name: fileName, contentType })
      } catch {
        message.error('Failed to load preview.')
      } finally {
        setPreviewLoading(false)
      }
    },
    [ruleId, ruleBase]
  )

  const downloadDoc = useCallback(
    async (docId: string, fileName: string) => {
      if (!ruleId) return
      try {
        const url = await getDocumentDownloadUrl(ruleId, docId, ruleBase)
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        a.target = '_blank'
        a.click()
      } catch {
        message.error('Failed to download file.')
      }
    },
    [ruleId, ruleBase]
  )

  const existingDocs = useMemo(() => {
    if (!ruleId) return []
    return documents.filter((d) => !staging.stagedDeleteIds.has(d.id))
  }, [ruleId, documents, staging.stagedDeleteIds])

  const stagedUploads = ruleId ? staging.stagedUploads : []
  const totalCount =
    existingDocs.length + stagedUploads.length + localFiles.length

  const handlePickFiles = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const handleFilesSelected = useCallback(
    (picked: File[]) => {
      if (ruleId) {
        picked.forEach((file) => staging.stageUpload(file, documents.length))
      } else {
        onAddLocal(picked)
      }
    },
    [ruleId, staging, documents.length, onAddLocal]
  )

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      multiple
      accept=".pdf,.csv,.xlsx,.docx,.png,.jpg,.jpeg,.webp"
      style={{ display: 'none' }}
      onChange={(e) => {
        const picked = Array.from(e.target.files ?? [])
        if (picked.length > 0) handleFilesSelected(picked)
        e.target.value = ''
      }}
    />
  )

  if (isLoading && ruleId) {
    return (
      <div>
        <div style={EVIDENCE_LABEL_STYLE}>Evidence</div>
        <div style={{ marginTop: 10 }}>
          <Spin size="small" />
        </div>
      </div>
    )
  }

  return (
    <div>
      <div style={{ ...EVIDENCE_LABEL_STYLE, marginBottom: 10 }}>Evidence</div>

      {totalCount === 0 ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            border: '1.5px dashed #d1d5db',
            borderRadius: 10,
            background: '#fafafa',
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: '#fff',
              border: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              color: '#9ca3af',
              flexShrink: 0,
            }}
          >
            <CloudUploadOutlined />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'rgba(0,0,0,0.85)',
              }}
            >
              No evidence attached
            </div>
            <div style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }}>
              Attach a PDF, screenshot, or compliance doc to support this
              override.
            </div>
          </div>
          {!isReadOnly && (
            <Button
              size="small"
              icon={<UploadOutlined />}
              onClick={handlePickFiles}
            >
              Upload
            </Button>
          )}
        </div>
      ) : (
        <>
          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              overflow: 'hidden',
              maxHeight: 180,
              overflowY: 'auto',
            }}
          >
            {existingDocs.map((doc) => (
              <EvidenceFileRow
                key={doc.id}
                thumbnail={
                  <div style={ICON_BOX_STYLE}>
                    {doc.contentType === 'application/pdf' ? (
                      <FilePdfOutlined />
                    ) : (
                      <FileOutlined />
                    )}
                  </div>
                }
                name={doc.fileName}
                meta={
                  doc.sizeBytes != null
                    ? formatFileSize(doc.sizeBytes)
                    : undefined
                }
                canPreview={canPreviewType(doc.contentType)}
                onPreview={() =>
                  openDocPreview(doc.id, doc.fileName, doc.contentType)
                }
                onDownload={() => downloadDoc(doc.id, doc.fileName)}
                onRemove={
                  isReadOnly ? undefined : () => staging.stageDelete(doc.id)
                }
              />
            ))}

            {stagedUploads.map((u) => (
              <EvidenceFileRow
                key={u.tempId}
                thumbnail={<FilePreview file={u.file} />}
                name={u.fileName}
                meta="Pending upload"
                canPreview={canPreviewType(u.file.type)}
                onPreview={() => openLocalPreview(u.file)}
                onDownload={() => downloadLocal(u.file)}
                onRemove={
                  isReadOnly ? undefined : () => staging.unstageUpload(u.tempId)
                }
              />
            ))}

            {localFiles.map((f, idx) => (
              <EvidenceFileRow
                key={idx}
                thumbnail={<FilePreview file={f} />}
                name={f.name}
                meta={formatFileSize(f.size)}
                canPreview={canPreviewType(f.type)}
                onPreview={() => openLocalPreview(f)}
                onDownload={() => downloadLocal(f)}
                onRemove={isReadOnly ? undefined : () => onRemoveLocal(idx)}
              />
            ))}
          </div>

          {!isReadOnly && (
            <Button
              size="small"
              icon={<UploadOutlined />}
              onClick={handlePickFiles}
              style={{ marginTop: 6 }}
            >
              Add more
            </Button>
          )}
        </>
      )}

      {hiddenInput}

      <EvidencePreviewModal
        preview={preview}
        loading={previewLoading}
        onClose={closePreview}
      />
    </div>
  )
}
